import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../../Navbar";
import {
  guestReservationsUrl, guestCancelReservationUrl,
} from "../../BaseURL";
import "../../styles/public.css";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday:"short", day:"numeric", month:"short", year:"numeric" });
};

export default function MyReservations() {
  const navigate = useNavigate();
  const guest    = JSON.parse(localStorage.getItem("guest") || "null");

  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState("");
  const [busy,    setBusy]    = useState(null);    // id of the reservation currently being cancelled
  const [msg,     setMsg]     = useState("");

  // Send unauthenticated visitors back to sign-in instead of showing a
  // blank page.  We use replace so the back button doesn't bring them
  // here in an infinite loop.
  useEffect(() => {
    if (!guest) navigate("/guest/login", { replace: true, state: { next: "/my-reservations" } });
  }, [guest, navigate]);

  const load = useCallback(async () => {
    setErr("");
    try {
      const res = await axios.get(guestReservationsUrl);
      setItems(res.data?.results || []);
    } catch (e) {
      console.error(e);
      setErr(e.response?.data?.message || "Could not load your reservations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (guest) load(); }, [guest, load]);

  const cancel = async (r) => {
    if (!window.confirm(`Cancel reservation ${r.code}?`)) return;
    setBusy(r.id);
    setMsg("");
    try {
      await axios.patch(guestCancelReservationUrl(r.id), { reason: "Cancelled by guest" });
      setMsg(`${r.code} cancelled.`);
      await load();
    } catch (e) {
      setErr(e.response?.data?.message || "Could not cancel.");
    } finally {
      setBusy(null);
    }
  };

  const signOut = () => {
    localStorage.removeItem("guest");
    navigate("/", { replace: true });
  };

  if (!guest) return null;

  return (
    <div className="pp-root">
      <div className="pp-bg" />
      <Navbar />
      <div className="pp-content">

        <div className="pp-page-head">
          <div className="pp-eyebrow">{guest.full_name}</div>
          <h1 className="pp-h1">Your <em>Reservations</em></h1>
          <p className="pp-tag">
            View what's pending, what's confirmed, and cancel any reservation that hasn't been confirmed yet.
          </p>
        </div>

        <div className="pp-filter-row">
          <Link to="/reserve" className="pp-btn-ghost"
                style={{ width: "auto", padding: "9px 18px", fontSize: 11 }}>
            + Book another stay
          </Link>
          <button onClick={signOut} className="pp-btn-ghost"
                  style={{ width: "auto", padding: "9px 18px", fontSize: 11, marginLeft: "auto" }}>
            Sign out
          </button>
        </div>

        {msg && <div className="pp-success">{msg}</div>}
        {err && <div className="pp-error">{err}</div>}

        {loading ? (
          <div className="pp-loading">Loading…</div>
        ) : items.length === 0 ? (
          <div style={{
            background:"var(--p-soft)", color:"#5a4838",
            padding:"40px 30px", borderRadius:12, textAlign:"center",
          }}>
            <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:24, color:"#2c1f14", marginBottom:8 }}>
              No reservations yet
            </div>
            <div style={{ fontSize:13, fontWeight:300, marginBottom:20 }}>
              When you reserve a room, it'll show up here.
            </div>
            <Link to="/reserve" className="pp-btn-ghost"
                  style={{ width:"auto", padding:"11px 22px", fontSize:11 }}>
              Browse rooms
            </Link>
          </div>
        ) : (
          items.map((r) => {
            const checkIn  = r.check_in_at  || r.check_in_date;
            const checkOut = r.check_out_at || r.check_out_date;
            return (
              <div key={r.id} className="pp-res-card">
                <div>
                  <div className="pp-res-code">{r.code}</div>
                  <div className="pp-res-line">
                    Room {r.room_number || "—"}
                    {r.room_type_name ? ` · ${r.room_type_name}` : ""}
                  </div>
                  <div className="pp-res-line">
                    {fmtDate(checkIn)} → {fmtDate(checkOut)} · {r.nights} night{r.nights === 1 ? "" : "s"}
                  </div>
                  <div className="pp-res-line">
                    Total · <strong>GH₵ {Number(r.total_amount || 0).toFixed(2)}</strong>
                    {Number(r.paid_amount) > 0
                      ? ` · Paid GH₵ ${Number(r.paid_amount).toFixed(2)}`
                      : ""}
                  </div>
                </div>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:10 }}>
                  <span className={`pp-res-status ${r.status}`}>{r.status.replace("_", " ")}</span>
                  {r.status === "pending" && (
                    <button onClick={() => cancel(r)}
                            disabled={busy === r.id}
                            className="pp-btn-ghost"
                            style={{ width:"auto", padding:"7px 14px", fontSize:10.5,
                                     color:"#a93226", borderColor:"rgba(192,57,43,.4)" }}>
                      {busy === r.id ? "Cancelling…" : "Cancel"}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
